import { useCallback, useMemo } from 'react';

/**
 * Custom hook to create stable event handlers for VideoPlayer
 * Prevents unnecessary re-renders by memoizing handlers
 */
export const useVideoPlayerEventHandlers = ({
  onReady = () => {},
  onStart = () => {},
  onPlay = () => {},
  onPause = () => {},
  onEnded = () => {},
}: {
  onReady?: (currentPlayerId: string) => void;
  onStart?: (currentPlayerId: string) => void;
  onPlay?: (currentPlayerId: string) => void;
  onPause?: (currentPlayerId: string) => void;
  onEnded?: (currentPlayerId: string) => void;
} = {}) => {
  // Create stable callbacks that reference the handlers
  const onReadyCallback = useCallback((playerId: string) => onReady(playerId), [onReady]);
  const onStartCallback = useCallback((playerId: string) => onStart(playerId), [onStart]);
  const onPlayCallback = useCallback((playerId: string) => onPlay(playerId), [onPlay]);
  const onPauseCallback = useCallback((playerId: string) => onPause(playerId), [onPause]);
  const onEndedCallback = useCallback((playerId: string) => onEnded(playerId), [onEnded]);

  const stableHandlers = useMemo(
    () => ({
      onReady: onReadyCallback,
      onStart: onStartCallback,
      onPlay: onPlayCallback,
      onPause: onPauseCallback,
      onEnded: onEndedCallback,
    }),
    [onReadyCallback, onStartCallback, onPlayCallback, onPauseCallback, onEndedCallback],
  );

  return stableHandlers;
};
